import { getTechnologyTypeFromCollection } from '../helpers';
import {
  BessItem,
  BESSProjectTechnology,
  ProjectTechnology,
  SolarItem,
  SolarProjectTechnology,
  TechnologyCollection,
  TechnologyDetails,
  TechnologyType,
  WindItem,
  WindProjectTechnology,
  BiogasItem,
  BiogasProjectTechnology,
  HydrogenItem,
  HydrogenProjectTechnology,
  HidropowerItem,
  HidropowerProjectTechnology,
  DatacenterItem,
  DatacenterProjectTechnology,
  TopcoProjectTechnology,
  TopcoItem,
  TopcoTechnologyDetails,
} from '../types';

export type TechnologyTransformerReturn = {
  id: number;
  project: string;
  type: TechnologyType;
  collection: TechnologyCollection;
  details: TechnologyDetails | TopcoTechnologyDetails;
};

export function technologyTransformer(technology: ProjectTechnology): TechnologyTransformerReturn {
  const technologyType = getTechnologyTypeFromCollection(technology.collection);
  return {
    id: technology.id,
    project: technology.project.id ?? technology.project,
    type: technologyType,
    collection: technology.collection,
    details: technologyDetailsTransformer({ technologyType, technology }),
  };
}

export type technologyTrasformerProps = {
  technologyType: TechnologyType;
  technology: ProjectTechnology;
};

export function technologyDetailsTransformer({
  technologyType,
  technology,
}: technologyTrasformerProps): TechnologyDetails | TopcoTechnologyDetails {
  if (technologyType === 'topco') {
    const item = technology.item as TopcoProjectTechnology;
    const topcoDetails: TopcoTechnologyDetails = {
      id: technology.id,
      files: technology.files?.map((file) => file.file) ?? [],
      item: {
        id: item.id,
        companyType: item.company_type,
        otherDetails: item.other_details,
        hqLocation: item.hq_location,
      }
    };
    return topcoDetails;
  }

  let item;
  const technologyDetails = {
    id: technology.id,
    developmentStatus: {
      status: 'development_status' in technology.item ? (technology.item.development_status ?? undefined) : undefined,
    },
    item: {} as
      | SolarItem
      | WindItem
      | BessItem
      | BiogasItem
      | HydrogenItem
      | HidropowerItem
      | DatacenterItem
      | TopcoItem,
    files: technology.files?.map((file) => file.file) ?? [],
    permittingPLA: {
      permits: technology.permits,
      comments: technology.permiting_comments,
    },
    landRights: {
      surface: technology.land_surface,
      grossBuiltArea: technology.land_gross_built_area,
      type: technology.land_types,
      price: technology.land_price,
      contractDuration: technology.land_contract_duration,
      contractedPercentage: technology.land_contracted_percentage,
      comments: technology.land_comments,
      dupStatus: technology.dup_status,
      dupDate: technology.dup_date,
      dupComments: technology.dup_comments,
    },
    connectionInfrastructure: {
      evacuationLineTotalDistance: technology.evacuation_line_total_distance,
      evacuationLineVoltage: technology.evacuation_line_voltage,
      connectionType: technology.connection_type,
      connectionTo: technology.connection_to,
      connectionCosts: technology.connection_costs,
      connectionCostsComments: technology.connection_costs_comments,
      sharedInfrastructure: technology.shared_infrastructure,
      sharedInfrastructureComments: technology.shared_infrastructure_comments,
      connectionInfrastructureComments: technology.connection_infrastructure_comments,
    },
    capexOpex: {
      estimatedEpc: technology.estimated_epc,
      epcComments: technology.epc_comments,
      omPrice: technology.om_price,
      omTerm: technology.om_term,
      omComments: technology.om_comments,
      insurancePrice: technology.insurance_price,
      insuranceComments: technology.insurance_comments,
    },
    others: {
      hasPpa: technology.has_ppa,
      ppaComments: technology.ppa_comments,
      hasDebt: technology.has_debt,
      debtComments: technology.debt_comments,
    },
  };

  switch (technologyType) {
    case 'solar':
      item = technology.item as SolarProjectTechnology;
      technologyDetails.item = {
        id: item.id,
        installedCapacityPeak: item.installed_capacity_peak,
        installedCapacityNominal: item.installed_capacity_nominal,
        specificProduction: item.specific_production,
        readyToBuild: item.ready_to_build,
        commercialOperationDate: item.commercial_operation_date,
        structureType: item.structure_type,
        moduleType: item.module_type,
        modulePower: item.module_power,
        panelConfiguration: item.panel_configuration,
        pitchDistance: item.pitch_distance,
        generalComments: item.comments,
        basicDesignComments: item.basic_design_comments,
      } as SolarItem;
      break;
    case 'wind':
      item = technology.item as WindProjectTechnology;
      technologyDetails.item = {
        id: item.id,
        installedCapacity: item.installed_capacity,
        specificProduction: item.specific_production,
        readyToBuild: item.ready_to_build,
        commercialOperationDate: item.commercial_operation_date,
        turbineModel: item.turbine_model,
        turbinePower: item.turbine_power,
        turbiness: item.turbiness,
        generalComments: item.comments,
        basicDesignComments: item.basic_design_comments,
      } as WindItem;
      break;
    case 'bess':
      item = technology.item as BESSProjectTechnology;
      technologyDetails.item = {
        id: item.id,
        installedCapacity: item.installed_capacity,
        readyToBuild: item.ready_to_build,
        commercialOperationDate: item.commercial_operation_date,
        storageTechnology: item.storage_technology,
        storageCapacity: item.storage_capacity,
        hoursCycles: item.hours_cycles,
        generalComments: item.comments,
        basicDesignComments: item.basic_design_comments,
        injectionCapacity: item.injection_capacity,
        consumptionCapacity: item.consumption_capacity,
      } as BessItem;
      break;
    case 'biogas':
      item = technology.item as BiogasProjectTechnology;
      technologyDetails.item = {
        id: item.id,
        capacity: item.capacity,
        ready_to_build: item.ready_to_build,
        commercial_operation_date: item.commercial_operation_date,
        feedstock_supply_radius: item.feedstock_supply_radius,
        feedstock_cost: item.feedstock_cost,
        upgrading_included: item.upgrading_included,
        upgrading_technology: item.upgrading_technology,
        information: item?.information,
      } as BiogasItem;
      break;
    case 'hydrogen':
      item = technology.item as HydrogenProjectTechnology;
      technologyDetails.item = {
        id: item.id,
        installedCapacity: item?.installed_capacity,
        information: item?.information,
      } as HydrogenItem;
      break;
    case 'hidropower':
      item = technology.item as HidropowerProjectTechnology;
      technologyDetails.item = {
        id: item.id,
        installedCapacity: item.installed_capacity,
        typeProject: item.type_project,
        storageHours: item.storage_hours,
        specificProduction: item.specific_production,
        developmentStatus: item.development_status,
        readyToBuild: item.ready_to_build,
        commercialOperationDate: item.commercial_operation_date,
        turbineCapacity: item.turbine_capacity,
        pumpCapacity: item.pump_capacity,
        generationStorageCapacity: item.generation_storage_capacity,
        generalComments: item.comments,
      } as HidropowerItem;
      break;
    case 'datacenter':
      item = technology.item as DatacenterProjectTechnology;
      technologyDetails.item = {
        id: item.id,
        installedCapacity: item.installed_capacity,
        typeProject: item.type_project,
        developmentStatus: item.development_status,
        readyToBuild: item.ready_to_build,
        //commercialOperationDate: item.commercial_operation_date,
        itCapacity: item.it_capacity,
        pue: item.pue,
        dataCenterType: item.data_center_type,
        redundancy: item.redundancy,
        coolingTechnology: item.cooling_technology,
        generalComments: item.comments,
      } as DatacenterItem;
      break;
  }

  technologyDetails.item.id = item.id;
  return technologyDetails;
}
